/**
 * Acesso do usuário logado: filtra o menu e guarda as rotas por módulo (migration 0058).
 *
 * Isto é a camada de UX. A barreira de verdade é o staff gate das Edge Functions, que aplica a
 * mesma regra (`packages/config/src/access`) em cada chamada — esconder um item aqui nunca
 * substitui isso, só evita mostrar o que a pessoa não pode usar.
 *
 * Sem dado (rota ainda não publicada, simulador ou carregando) o padrão é PERMISSIVO na UI; o
 * servidor continua decidindo. Conta bloqueada é o único caso que troca a tela inteira.
 */
import React, { createContext, useContext, type ReactElement, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { StatusPanel } from "../components/StatusPanel.js";
import { useMyAccess } from "../queries.js";
import { useStaffSession } from "./StaffSessionBoundary.js";
import { levelFor, moduleForPath, type AccessLevel, type StaffRoleKey } from "../../../../packages/config/src/access.js";
import type { StaffUser } from "../users/model.js";
import { AI_OWNER_EMAIL } from "../../../../packages/ai-core/src/ai-settings.js";

interface AccessState {
  user: StaffUser | null;
}

const AccessContext = createContext<AccessState>({ user: null });

/** Rotas exclusivas de administrador, mesmo dentro do módulo "Sistema". */
const ADMIN_ONLY_PREFIXES = ["/configuracoes/usuarios"];

/** Rotas de uma pessoa só: chaves e parâmetros da IA (e-mail do dono, mesma regra da Edge Function). */
const OWNER_ONLY_PREFIXES = ["/configuracoes/ia"];

export function levelForPath(user: StaffUser | null, pathname: string): AccessLevel {
  if (!user) return "edit";
  if (OWNER_ONLY_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) return user.email.toLowerCase() === AI_OWNER_EMAIL ? "edit" : "none";
  if (ADMIN_ONLY_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) return user.role === "admin" ? "edit" : "none";
  const module = moduleForPath(pathname);
  // Rota fora do mapa de módulos: não restringe (ex.: 404).
  if (!module) return "edit";
  return levelFor(user.role as StaffRoleKey, user.permissions, module);
}

export function useAccessLevelFor(): (pathname: string) => AccessLevel {
  const { user } = useContext(AccessContext);
  return (pathname) => levelForPath(user, pathname);
}

export function AccessProvider({ children }: { children: ReactNode }): ReactElement {
  const { signOut } = useStaffSession();
  const query = useMyAccess();
  if (query.isError && /^user_blocked/.test(query.error.message)) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6" data-screen="conta-bloqueada">
        <div className="flex max-w-md flex-col gap-4 rounded-panel border border-danger bg-surface p-6 text-center">
          <h1 className="m-0 text-xl font-extrabold text-ink">Conta bloqueada</h1>
          <p className="m-0 text-body">O acesso desta conta foi suspenso por um administrador. Fale com quem gerencia a equipe.</p>
          <button type="button" onClick={signOut} className="min-h-[44px] rounded-control border border-border-strong bg-surface px-4 font-bold text-ink">
            Sair
          </button>
        </div>
      </main>
    );
  }
  return <AccessContext.Provider value={{ user: query.data ?? null }}>{children}</AccessContext.Provider>;
}

/** Envolve as rotas: sem acesso mostra aviso; só leitura mostra faixa informativa. */
export function RouteGuard({ children }: { children: ReactNode }): ReactElement {
  const { pathname } = useLocation();
  const level = useAccessLevelFor()(pathname);
  if (level === "none") {
    return <StatusPanel kind="error" title="Você não tem acesso a esta área" detail="Peça a um administrador para liberar este módulo para a sua conta." />;
  }
  return (
    <>
      {level === "view" ? (
        <p role="status" className="m-0 mb-4 rounded-panel border border-warning bg-warning-soft px-4 py-2 text-sm text-ink" data-access-banner="view">
          Você tem acesso somente leitura a este módulo: pode consultar, mas não alterar.
        </p>
      ) : null}
      {children}
    </>
  );
}
